import React, { ReactNode, useState } from "react";
import { TabGroup, Tab, TabList, TabPanel, TabPanels } from "@headlessui/react";

interface TabItem {
  label?: ReactNode;
  content: ReactNode;
  icon?: ReactNode;
}

interface TabGroupProps {
  tabs: TabItem[];
  variant?: "underline" | "contained";
  onTabChange?: (index: number) => void;
  defaultSelectedIndex?: number;
  alwaysSelectTab?: number;
  className?: string;
  /** When provided, component operates in controlled mode */
  selectedIndex?: number;
}

function CustomTabGroup({
  tabs,
  variant = "underline",
  onTabChange,
  defaultSelectedIndex = null,
  alwaysSelectTab = null,
  className = "",
  selectedIndex: controlledSelectedIndex,
}: TabGroupProps) {
  const [internalSelectedIndex, setInternalSelectedIndex] = useState<number>(
    defaultSelectedIndex ?? 0,
  );

  // Use controlled index if provided, otherwise use internal state
  const isControlled = controlledSelectedIndex !== undefined;
  const selectedIndex = isControlled
    ? controlledSelectedIndex
    : internalSelectedIndex;

  const getTabStyles = (selected: boolean) => {
    if (variant === "contained") {
      return `flex items-center justify-center px-4 py-2 rounded-lg text-sm font-medium focus:outline-none ${
        selected
          ? "bg-electrum text-white"
          : "bg-gray-100 text-gray-500 hover:bg-gray-200"
      }`;
    }

    return `flex flex-col items-center text-center text-sm font-medium flex-1 focus:outline-none ${
      selected
        ? "text-electrum-300 border-b-2 border-electrum-300"
        : "text-electrum-200"
    }`;
  };

  const handleTabClick = (index: number) => {
    // If alwaysSelectTab is set, never allow deselection
    if (alwaysSelectTab !== null) {
      if (!isControlled) {
        setInternalSelectedIndex(index);
      }
      onTabChange?.(index);
      return;
    }

    // For normal behavior, don't deselect when clicking the same tab
    if (!isControlled) {
      setInternalSelectedIndex(index);
    }
    onTabChange?.(index);
  };

  return (
    <TabGroup
      selectedIndex={selectedIndex}
      onChange={(index) => handleTabClick(index)}
      className={`w-full h-full flex flex-col min-h-0 ${className}`}
    >
      <TabList
        className={`mt-3 flex w-full ${
          variant === "contained"
            ? "gap-4 justify-center"
            : "justify-between border-b-2 border-gray-200"
        }`}
      >
        {tabs.map((tab, index) => (
          <Tab
            key={`tab-${index}`}
            className={getTabStyles(selectedIndex === index)}
            onClick={(e) => {
              e.preventDefault();
              handleTabClick(index);
            }}
          >
            {tab.icon && tab.icon}
            {tab.label && <span>{tab.label}</span>}
          </Tab>
        ))}
      </TabList>
      <TabPanels className="mt-2 w-full flex-1 min-h-0 overflow-hidden flex flex-col">
        {tabs.map((tab, index) => (
          <TabPanel
            key={`tab-panel-${index}`}
            className="w-full h-full min-h-0 overflow-auto"
          >
            {selectedIndex === index && tab.content}
          </TabPanel>
        ))}
      </TabPanels>
    </TabGroup>
  );
}

export default CustomTabGroup;
